const brainGameLevels = require('./brainGameLevels');

const { BRAIN_GAME_LEVEL_CONFIG } = brainGameLevels;

const BRAIN_GAME_HIGH_SCORE_METRICS = Object.freeze({
    SCORE: 'score',
    STREAK: 'streak',
    ELAPSED_MS: 'elapsedMs',
    LEVEL: 'level',
});
const VALID_METRICS = new Set(Object.values(BRAIN_GAME_HIGH_SCORE_METRICS));

const normalizeBrainGameHighScore = (record = {}) => {
    const metric = `${record?.metric || ''}`.trim();
    const value = Number(record?.value);

    if (!VALID_METRICS.has(metric) || !Number.isFinite(value)) {
        return null;
    }

    if (metric === BRAIN_GAME_HIGH_SCORE_METRICS.ELAPSED_MS && value <= 0) {
        return null;
    }

    return {
        metric,
        value: metric === BRAIN_GAME_HIGH_SCORE_METRICS.ELAPSED_MS
            ? Math.max(1, Math.round(value))
            : Math.round(value),
        achievedAt: typeof record?.achievedAt === 'string' ? record.achievedAt : '',
    };
};

const createBrainGameHighScores = (rawHighScores = {}) => Object.fromEntries(
    Object.keys(BRAIN_GAME_LEVEL_CONFIG)
        .map((gameId) => [gameId, normalizeBrainGameHighScore(rawHighScores?.[gameId])])
        .filter(([, record]) => record !== null)
);

const selectBrainGameHighScoreCandidate = (hud = {}, metrics = {}, achievedAt = new Date()) => {
    const candidates = [
        hud?.score === true && Number.isFinite(Number(metrics?.score))
            ? { metric: BRAIN_GAME_HIGH_SCORE_METRICS.SCORE, value: Number(metrics.score) }
            : null,
        hud?.streak === true && Number.isFinite(Number(metrics?.streak))
            ? { metric: BRAIN_GAME_HIGH_SCORE_METRICS.STREAK, value: Number(metrics.streak) }
            : null,
        hud?.elapsed === true && Number.isFinite(Number(metrics?.elapsedMs)) && Number(metrics.elapsedMs) > 0
            ? { metric: BRAIN_GAME_HIGH_SCORE_METRICS.ELAPSED_MS, value: Number(metrics.elapsedMs) }
            : null,
        Number.isFinite(Number(metrics?.level)) && Number(metrics.level) > 0
            ? { metric: BRAIN_GAME_HIGH_SCORE_METRICS.LEVEL, value: Number(metrics.level) }
            : null,
    ].find(Boolean);

    if (!candidates) {
        return null;
    }

    const date = achievedAt instanceof Date ? achievedAt : new Date(achievedAt);
    const safeDate = Number.isFinite(date.getTime()) ? date : new Date();

    return normalizeBrainGameHighScore({
        ...candidates,
        achievedAt: safeDate.toISOString(),
    });
};

const isBrainGameHighScoreImprovement = (candidate, previousBest) => {
    if (!candidate || !previousBest || candidate.metric !== previousBest.metric) {
        return false;
    }

    if (candidate.metric === BRAIN_GAME_HIGH_SCORE_METRICS.ELAPSED_MS) {
        return candidate.value < previousBest.value;
    }

    return candidate.value > previousBest.value;
};

const setBrainGameHighScore = (rawHighScores, gameId, rawRecord) => {
    const normalizedGameId = `${gameId || ''}`.trim();
    const highScores = createBrainGameHighScores(rawHighScores);
    const record = normalizeBrainGameHighScore(rawRecord);

    if (!BRAIN_GAME_LEVEL_CONFIG[normalizedGameId] || !record) {
        return highScores;
    }

    const previousBest = highScores[normalizedGameId] || null;
    const shouldReplace = previousBest === null
        || previousBest.metric !== record.metric
        || isBrainGameHighScoreImprovement(record, previousBest);

    return {
        ...highScores,
        [normalizedGameId]: shouldReplace ? record : previousBest,
    };
};

const evaluateBrainGameHighScore = (
    rawHighScores,
    gameId,
    hud = {},
    metrics = {},
    achievedAt = new Date()
) => {
    const normalizedGameId = `${gameId || ''}`.trim();
    const highScores = createBrainGameHighScores(rawHighScores);

    if (!BRAIN_GAME_LEVEL_CONFIG[normalizedGameId]) {
        return {
            best: null,
            candidate: null,
            isFirstPersonalBest: false,
            isNewHighScore: false,
            nextHighScores: highScores,
            previousBest: null,
        };
    }

    const previousBest = highScores[normalizedGameId] || null;
    const candidate = selectBrainGameHighScoreCandidate(hud, metrics, achievedAt);

    if (!candidate) {
        return {
            best: previousBest,
            candidate: null,
            isFirstPersonalBest: false,
            isNewHighScore: false,
            nextHighScores: highScores,
            previousBest,
        };
    }

    const isComparable = previousBest?.metric === candidate.metric;
    const isFirstPersonalBest = previousBest === null || !isComparable;
    const isNewHighScore = isBrainGameHighScoreImprovement(candidate, previousBest);
    const best = isFirstPersonalBest || isNewHighScore ? candidate : previousBest;

    return {
        best,
        candidate,
        isFirstPersonalBest,
        isNewHighScore,
        nextHighScores: {
            ...highScores,
            [normalizedGameId]: best,
        },
        previousBest,
    };
};

const formatBrainGameHighScoreValue = (record) => {
    const normalized = normalizeBrainGameHighScore(record);

    if (!normalized) {
        return '';
    }

    if (normalized.metric !== BRAIN_GAME_HIGH_SCORE_METRICS.ELAPSED_MS) {
        return `${normalized.value}`;
    }

    const totalSeconds = Math.max(0, Math.round(normalized.value / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = `${totalSeconds % 60}`.padStart(2, '0');

    return `${minutes}:${seconds}`;
};

module.exports = {
    BRAIN_GAME_HIGH_SCORE_METRICS,
    createBrainGameHighScores,
    evaluateBrainGameHighScore,
    formatBrainGameHighScoreValue,
    isBrainGameHighScoreImprovement,
    normalizeBrainGameHighScore,
    selectBrainGameHighScoreCandidate,
    setBrainGameHighScore,
};
